// --- Our Modules --------------------------------------------------------------------------------
import type { TyBranchDetailPrice, TyProduct } from "@/types";
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type BranchProductDiffPopupDetails = {
    product: TyProduct;
    branchEntry: TyBranchDetailPrice | null;
    isInactiveAtBranch: boolean;
    isHiddenAtBranch: boolean;
};

type Props = {
    onClose: () => void;
    details?: BranchProductDiffPopupDetails;
};

// --- Helpers ------------------------------------------------------------------------------------

type PriceChangeKind = "amount" | "label-hidden" | "label-shown" | "hidden";

type PriceDiff = {
    price: TyProduct["prices"][number];
    kind: PriceChangeKind[];
    overrideAmount?: number;
};

function buildPriceDiffs(product: TyProduct, branchEntry: TyBranchDetailPrice | null): PriceDiff[] {
    const priceOverrides = branchEntry?.prices ?? [];
    const hiddenPriceIds = branchEntry?.hiddenPrices ?? [];

    return product.prices.reduce<PriceDiff[]>((acc, price) => {
        const isHidden = hiddenPriceIds.includes(price._id);
        const override = priceOverrides.find((o) => o._id === price._id);

        const kind: PriceChangeKind[] = [];

        if (isHidden) {
            kind.push("hidden");
        } else if (override !== undefined) {
            if (override.amount !== price.amount) kind.push("amount");
            if (override.showLabel !== undefined && override.showLabel !== price.showLabel) {
                kind.push(override.showLabel ? "label-shown" : "label-hidden");
            }
        }

        if (kind.length > 0) {
            acc.push({ price, kind, overrideAmount: override?.amount });
        }
        return acc;
    }, []);
}

function discountChanged(product: TyProduct, branchDiscount: TyBranchDetailPrice["discount"]): boolean {
    if (branchDiscount === null) return false;
    const pd = product.discount;
    if (!pd?.active && !branchDiscount.active) return false;
    if ((pd?.active ?? false) !== branchDiscount.active) return true;
    if (!pd?.active || !branchDiscount.active) return false;
    return pd.type !== branchDiscount.type || pd.amount !== branchDiscount.amount;
}

// --- Logic --------------------------------------------------------------------------------------

export default function BranchProductDiffPopup({ onClose, details }: Props) {
    const t = useTranslation();

    if (!details) return null;

    const { product, branchEntry, isInactiveAtBranch, isHiddenAtBranch } = details;

    const priceDiffs = buildPriceDiffs(product, branchEntry);
    const hasDiscountChange = discountChanged(product, branchEntry?.discount ?? null);
    const branchDiscount = branchEntry?.discount ?? null;

    const discountLabel = (d: { active: boolean; type: "fixed" | "percentage"; amount: number } | null) => {
        if (!d?.active) return t.branchMenu.discountRemovedBadge;
        return d.type === "percentage" ? `${d.amount}%` : `${d.amount}`;
    };

    return (
        <div className="bpdp">
            <div className="bpdp-header">
                <div className="bpdp-header-text">
                    <p className="bpdp-title">{t.branchMenu.diffTitle}</p>
                    <p className="bpdp-subtitle">{product.name}</p>
                </div>
                <button type="button" className="bpdp-close" onClick={onClose} aria-label="Close">
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <div className="bpdp-body">
                {/* ── Availability changes ── */}
                {(isInactiveAtBranch || isHiddenAtBranch) && (
                    <div className="bpdp-section">
                        <p className="bpdp-section-title">
                            <img src="/icons/inputs-group-icon.svg" alt="" width={14} height={14} />
                            {t.branchMenu.activeHereLabel.replace("؟", "").replace("?", "")}
                        </p>
                        {isInactiveAtBranch && (
                            <div className="bpdp-row bpdp-row--warning">
                                <span className="bpdp-row-label">{t.branchMenu.diffInactiveLabel}</span>
                            </div>
                        )}
                        {isHiddenAtBranch && (
                            <div className="bpdp-row bpdp-row--warning">
                                <span className="bpdp-row-label">{t.branchMenu.hiddenHereLabel.replace("؟", "").replace("?", "")}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* ── Price changes: one row per changed price only ── */}
                {priceDiffs.length > 0 && (
                    <div className="bpdp-section">
                        <p className="bpdp-section-title">
                            <img src="/icons/inputs-group-icon.svg" alt="" width={14} height={14} />
                            {t.formProduct.pricesSection}
                        </p>
                        {priceDiffs.map(({ price, kind, overrideAmount }) => (
                            <div key={price._id} className={`bpdp-row${kind.includes("hidden") ? " bpdp-row--hidden" : ""}`}>
                                {price.showLabel && <span className="bpdp-row-label">{price.label}</span>}

                                {kind.includes("hidden") && (
                                    <span className="bpdp-row-badge bpdp-row-badge--red">{t.branchMenu.priceHiddenBadge}</span>
                                )}

                                {kind.includes("amount") && (
                                    <div className="bpdp-row-diff">
                                        <span className="bpdp-diff-original">{price.amount}</span>
                                        <img src="/icons/arrow-dark-icon.svg" alt="→" width={12} height={12} className="bpdp-diff-arrow" />
                                        <span className="bpdp-diff-new">{overrideAmount}</span>
                                    </div>
                                )}

                                {(kind.includes("label-hidden") || kind.includes("label-shown")) && (
                                    <span className="bpdp-row-badge bpdp-row-badge--gray">
                                        {kind.includes("label-hidden") ? t.branchMenu.labelHiddenBadge : t.branchMenu.activeHereLabel}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* ── Discount change (only when actually different) ── */}
                {hasDiscountChange && (
                    <div className="bpdp-section">
                        <p className="bpdp-section-title">
                            <img src="/icons/inputs-group-icon.svg" alt="" width={14} height={14} />
                            {t.formProduct.discountSection}
                        </p>
                        <div className="bpdp-row">
                            <div className="bpdp-row-diff">
                                <span className="bpdp-diff-original">{discountLabel(product.discount)}</span>
                                <img src="/icons/arrow-dark-icon.svg" alt="→" width={12} height={12} className="bpdp-diff-arrow" />
                                <span className="bpdp-diff-new">{discountLabel(branchDiscount)}</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
